/** Where the UI language is remembered in the browser.
 *
 *  Only an explicit choice is ever written. Detection results are never
 *  cached: the old i18next detector wrote whatever it resolved (mostly
 *  "en" for browsers in languages we did not support) under
 *  ``repoworks.locale``, and that cached guess then beat the browser
 *  setting forever — the caching trap. Every access is wrapped because
 *  storage throws in private windows and with blocked site data. */

export const UI_LANGUAGE_KEY = "repoworks.uiLanguage";
/** Set when the choice was made signed-out, so it can be uploaded to
 *  the account after login. */
export const PENDING_SYNC_KEY = "repoworks.uiLanguage.pendingSync";
export const LEGACY_LOCALE_KEY = "repoworks.locale";

/** The only non-English locales that existed while the legacy key was
 *  written. A legacy "en" is dropped: it was mostly auto-resolved from
 *  an unsupported browser language, not chosen. */
const LEGACY_MIGRATED = new Set(["sv", "fi"]);

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function read(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    storage()?.setItem(key, value);
  } catch {
    // Storage full or blocked: the choice still applies to this page.
  }
}

function remove(key: string): void {
  try {
    storage()?.removeItem(key);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
}

/** The explicitly chosen UI language, or null. */
export function readStoredUiLanguage(): string | null {
  return read(UI_LANGUAGE_KEY);
}

/** One-time move from the detector's ``repoworks.locale``. Runs before
 *  detection on every boot; the legacy key is removed in all cases. */
export function migrateLegacyLocale(): void {
  const legacy = read(LEGACY_LOCALE_KEY);
  if (legacy === null) return;
  const base = legacy.trim().split(/[-_]/)[0]?.toLowerCase() ?? "";
  if (LEGACY_MIGRATED.has(base) && read(UI_LANGUAGE_KEY) === null) {
    write(UI_LANGUAGE_KEY, base);
  }
  remove(LEGACY_LOCALE_KEY);
}

/** Remember a language the user picked. Signed-out choices are flagged
 *  for upload to the account after login. */
export function rememberExplicitChoice(
  code: string,
  { authenticated }: { authenticated: boolean },
): void {
  write(UI_LANGUAGE_KEY, code);
  if (authenticated) remove(PENDING_SYNC_KEY);
  else write(PENDING_SYNC_KEY, "1");
}

export function hasPendingSync(): boolean {
  return read(PENDING_SYNC_KEY) === "1";
}

export function clearPendingSync(): void {
  remove(PENDING_SYNC_KEY);
}
